'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import { allowedAuth, tenant } from '@/interfaces/common';
import { otpFrom } from '@/utils/constants';
import { useGetErrorMessage } from '@/utils/dbMessagesHelper';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

import { email_template_config } from '@/config/email_template.config';
import useEmailCheck from '@/hooks/useEmailCheck';
import useOtpSend from '@/hooks/useOtpSend';
import { useTranslations } from '@/hooks/useTranslations';

import EmailForm from './EmailForm';
import PasswordAndOtpForm from './PasswordAndOtpForm';

const LoginForm = ({
  shouldShowContinueWithSSOButton,
  ssoLink,
  tenant,
  clientId,
  app_id,
}: {
  shouldShowContinueWithSSOButton: boolean;
  ssoLink: string;
  clientId: string;
  tenant: tenant;
  app_id?: string;
}) => {
  // setting the tenantId in the session storage for the linkedin login.
  if (tenant?._id !== undefined && typeof window !== 'undefined') {
    window.sessionStorage.setItem('tenantId', tenant?._id);
    if (clientId !== undefined && clientId !== '' && clientId !== null) {
      window.sessionStorage.setItem('clientId', clientId);
    }
  }

  const { translations, isLoading } = useTranslations('auth_page');

  const { getDBMessage } = useGetErrorMessage();

  const ready_to_explore =
    translations?.ready_to_explore || 'Hello! Ready to Explore?';
  const great_to_see_you =
    translations?.great_to_see_you || 'Great to See You Again!';

  // Error and success message translations
  const not_signed_up_message =
    translations?.error_messages?.not_signed_up ||
    'You are not signed up with this tenant, please signup first with this tenant';
  const otp_sent_to_mail =
    translations?.success_messages?.otp_sent_to_mail ||
    'OTP sent to your mail.';

  const [isEmailForm, setIsEmailForm] = useState(true);
  const [email, setEmail] = useState('');
  const [signUpWith, setSignUpWith] = useState('');
  const [allowedAuth, setAllowedAuth] = useState<allowedAuth>({
    otp: tenant?.otp,
    linkedin: tenant?.linkedin,
    password: tenant?.passwordBasedAuth,
    sso: tenant?.singleSignOnChecked,
  });
  const [errorMsg, setErrorMsg] = useState('');

  const REQUEST_FROM_SIGNUP = false;
  const {
    loading: emailCheckLoading,
    data,
    handleClick: checkEmail,
    error: emailCheckError,
  } = useEmailCheck({ REQUEST_FROM_SIGNUP });

  const {
    loading: otpSendLoading,
    data: otpResponse,
    handleClick: sendOtp,
    error: otpSendError,
  } = useOtpSend(email_template_config.SIGNIN_OTP_TEMPLATE);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSignUpWith('');
    if (isEmailForm) {
      checkEmail(email, tenant?._id, app_id);
    }
  };

  useEffect(() => {
    if (tenant) {
      setAllowedAuth({
        otp: tenant?.otp,
        linkedin: tenant?.linkedin,
        password: tenant?.passwordBasedAuth,
        sso: tenant?.singleSignOnChecked,
      });
    } else {
      setAllowedAuth({
        linkedin: false,
        otp: true,
        password: false,
        sso: false,
      });
    }
  }, [tenant]);

  useEffect(() => {
    if (emailCheckError) {
      toast.error(getDBMessage(emailCheckError) || emailCheckError);
      setErrorMsg(getDBMessage(emailCheckError) || emailCheckError);
    }
    if (otpSendError) {
      toast.error(getDBMessage(otpSendError) || otpSendError);
    }
  }, [emailCheckError, otpSendError]);

  useEffect(() => {
    if (data && data.email) {
      if (data?.isUserInSameTenant) {
        setSignUpWith(data.signUpWith);

        if (data.isPasswordEnabled === true) {
          setIsEmailForm(false);
          setErrorMsg('');
        }
      } else {
        toast.error(not_signed_up_message);
        setErrorMsg(not_signed_up_message);
      }
    }
  }, [data, not_signed_up_message]);

  useEffect(() => {
    if (
      !data?.isPasswordEnabled &&
      (signUpWith === 'otp' || signUpWith === null)
    ) {
      sendOtp(email, tenant?._id, otpFrom.SIGNIN);
    }
  }, [signUpWith, data]);

  useEffect(() => {
    if (otpResponse) {
      setErrorMsg('');
      toast.success(otp_sent_to_mail);
      setIsEmailForm(false);
    }
  }, [otpResponse, otp_sent_to_mail]);

  useEffect(() => {
    if (!email) {
      setErrorMsg('');
    }
  }, [email]);

  // Check if this is an Infineon tenant (SSO only)
  const isInfineonTenant = tenant?.tenantName?.toLowerCase() === 'infineon';

  // For Infineon tenants, show only SSO button
  if (isInfineonTenant && shouldShowContinueWithSSOButton) {
    const handleSSOClick = () => {
      window.location.href = ssoLink;
    };

    return (
      <div className="infineon-theme flex h-[calc(100vh-48px)] items-center justify-center overflow-y-auto rounded-3xl bg-white">
        <div className="w-[384px] text-center">
          {tenant?.logo && (
            <div className="mb-8">
              <Image
                src={tenant?.logo}
                alt={tenant?.tenantName}
                width={120}
                height={53}
                className="mx-auto"
              />
            </div>
          )}
          <div className="mb-8">
            {isLoading ? (
              <div className="h-10 w-64 animate-pulse rounded bg-gray-200 mx-auto" />
            ) : (
              // <h1 className="text-3xl font-bold">Welcome to Infineon Live Lab!</h1>
              <h1></h1>
            )}
          </div>
          <Button
            onClick={handleSSOClick}
            size="fullWidth"
            className="infineon-primary"
          >
            Continue with Infineon SSO
          </Button>
          <p className="mt-6 text-center text-xs text-gray-500">
            By continuing, you agree to TenXer Labs&apos;{' '}
            <a
              href="https://tenxerlabs.com/terms-conditions/"
              target="_blank"
              className="underline"
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a
              target="_blank"
              className="underline"
              href="https://tenxerlabs.com/privacy-policy/"
            >
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-48px)] items-center justify-center overflow-y-auto rounded-3xl bg-white">
      <div className="w-[384px]">
        {tenant?.logo && (
          <Image
            src={tenant?.logo}
            alt={tenant?.tenantName}
            width={120}
            height={53}
          />
        )}
        <div className={`${isEmailForm ? 'my-12 mb-8' : 'mb-1 mt-12'}`}>
          {isLoading ? (
            <div className="h-10 w-64 animate-pulse rounded bg-gray-200" />
          ) : (
            <h1 className="text-3xl font-bold">
              {isEmailForm ? ready_to_explore : great_to_see_you}
            </h1>
          )}
        </div>
        {!isEmailForm && (
          <p className="mb-5 text-base font-semibold text-zinc-500">{email}</p>
        )}
        {isEmailForm && (
          <EmailForm
            email={email}
            setEmail={setEmail}
            handleSubmit={handleSubmit}
            loading={emailCheckLoading || otpSendLoading}
            allowedAuth={allowedAuth}
            tenantId={tenant?._id}
            errorMsg={errorMsg}
            shouldShowContinueWithSSOButton={shouldShowContinueWithSSOButton}
            ssoLink={ssoLink}
          />
        )}
        {!isEmailForm && (
          <PasswordAndOtpForm
            email={email}
            signUpWith={signUpWith}
            isPasswordEnabled={data?.isPasswordEnabled}
            setIsEmailForm={setIsEmailForm}
            tenantId={tenant?._id}
            isOtpEnable={data?.isOtpEnable ?? true}
            shakey={data?.shakey ?? ''}
          />
        )}
      </div>
    </div>
  );
};

export default LoginForm;
